import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { action, computed, observable } from 'mobx'
import { toStream } from '@/utils/mobx-util'
import { combineLatestWith, filter, type Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { shareLatest, unpair, whileActive } from '@/utils/rx-util'

/**
 * Factory for creating a step view model.
 */
export type StepFactory = (reader: StepViewModelStateReader) => StepViewModel

/**
 * Step navigation direction, used for animation purposes.
 */
export type StepNavigationDirection = 'Forward' | 'Backward'

/**
 * View-Model that keeps track of which step is the current one
 * and allows navigating between them.
 */
export class OutlineViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The currently active step.
   *
   * @private
   */
  @observable private accessor _activeStep: StepViewModel | null = null

  /**
   * Whether we're currently navigating forward or backward.
   */
  @observable accessor navigationDirection: StepNavigationDirection = 'Forward'

  /**
   * The active step as a stream.
   */
  readonly activeStep$

  constructor(
    isPrepared$: Observable<boolean>,
    private readonly _getStepAccessors: () => StepAccessor[],
  ) {
    this.createStep = this.createStep.bind(this)

    this.activeStep$ = toStream(() => this.activeStep, true).pipe(shareLatest())

    // Whenever we lose the active step, request the
    // next pending step to become active.
    this._lifecycle.manage(
      this.activeStep$.pipe(
        // Only run this pipeline after the state slices have
        // been fully prepared. This ensures that we've identified
        // all currently available steps so we know which one
        // to default to.
        whileActive(isPrepared$),
        // Re-evaluate whenever the step accessors change
        combineLatestWith(toStream(() => this.stepAccessors, true)),
        unpair(),
        filter((v) => v === null),
        tap(this.requestPendingStepBecomeActive),
      ),
    )
  }

  /**
   * Gets the previous step.
   */
  @computed
  get previousStep() {
    const current = this.activeStep
    if (!current) {
      return
    }

    return this.stepAccessors[this._indexOf(current) - 1]
  }

  /**
   * Gets the next step.
   */
  @computed
  get nextStep() {
    const current = this.activeStep
    if (!current) {
      return
    }

    return this.stepAccessors[this._indexOf(current) + 1]
  }

  /**
   * All step accessors.
   *
   * The draft has multiple sections (questions, previous Pro, checkout), and
   * each may have multiple steps. By wrapping in an accessor, each section
   * can dynamically return however many steps are relevant for that section.
   */
  @computed
  get stepAccessors() {
    return this._getStepAccessors()
  }

  /**
   * The currently active step.
   *
   * Returns `null` if the active one is not relevant.
   */
  @computed
  get activeStep(): StepViewModel | null {
    const step = this._activeStep

    if (!step) {
      return null
    }

    if (this._indexOf(step) === -1) {
      return null
    }

    return step
  }

  /**
   * Gets the completion progress.
   */
  @computed.struct
  get progress(): Progress {
    const currentStep = (this.activeStep?.position.index ?? 0) + 1
    const total = this.stepAccessors.length
    const completed = this.stepAccessors.filter(
      (s) => s.step.isCompleted,
    ).length
    const percent = Math.round((completed / total) * 100)

    return {
      currentStep,
      total,
      completed,
      percent,
    }
  }

  /**
   * Gets the step with the specified ID.
   *
   * @param stepId
   */
  getStepById(stepId: string): StepViewModel | null {
    return (
      this.stepAccessors.find(
        (stepContainer) => stepContainer.step.stepId === stepId,
      )?.step ?? null
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Goes to the previous step.
   */
  @action.bound
  goToPrevious() {
    this.previousStep?.step.becomeActive()
  }

  /**
   * Goes to the next step.
   */
  @action.bound
  goToNext() {
    this.nextStep?.step.becomeActive()
  }

  /**
   * Finds the first incomplete step and activates it if there is any.
   * If not, activates the last step.
   */
  @action.bound
  requestPendingStepBecomeActive() {
    if (this.stepAccessors.length === 0) {
      return
    }

    const accessor =
      this.stepAccessors.find((c) => !c.step.isCompleted) ??
      this.stepAccessors[this.stepAccessors.length - 1]
    accessor?.step.becomeActive()
  }

  /**
   * Creates a step with the specified adapter.
   * Injects other functionality needed
   *
   * @param adapter
   */
  createStep(adapter: StepViewModelStateReader): StepViewModel {
    return new StepViewModel({
      ...adapter,
      activeStep: () => this.activeStep,
      positionOf: (x) => this._positionOf(x),
      setActiveStep: (x, direction) => this._setActiveStep(x, direction),
    })
  }

  /**
   * Sets the active step.
   *
   * @private
   */
  @action.bound
  private _setActiveStep(
    step: StepViewModel,
    direction?: StepNavigationDirection,
  ) {
    if (this._activeStep === step) {
      return
    }

    const nextIndex = this._indexOf(step)
    const prevIndex = this._activeStep?.position.index ?? nextIndex
    if (nextIndex > -1) {
      this._activeStep = step
      this.navigationDirection =
        direction ?? (nextIndex >= prevIndex ? 'Forward' : 'Backward')
    }
  }

  /**
   * Finds the index of the specified step.
   *
   * @param step
   * @private
   */
  private _indexOf(step: StepViewModel) {
    return this.stepAccessors.findIndex(
      (stepContainer) => stepContainer.step === step,
    )
  }

  /**
   * Gets the step position info for the specified step.
   *
   * @param step
   */
  private _positionOf(step: StepViewModel): StepPosition {
    const index = this._indexOf(step)
    return {
      index,
      first: index === 0,
      last: index === this.stepAccessors.length - 1,
    }
  }
}

/**
 * An object that has a `step` getter and represents a step in the flow.
 */
export interface StepAccessor {
  /**
   * The step view model.
   */
  readonly step: StepViewModel
}

/**
 * Display info for an Outline item.
 */
export interface OutlineItemDisplay {
  /**
   * The title of the step.
   */
  readonly title: string
}

/**
 * Step-specific getters.
 */
export interface StepViewModelStateReader {
  /**
   * Identifier for the step.
   */
  readonly stepId: string

  /**
   * Getter for what to display on the outline item.
   */
  outlineItemDisplay(): OutlineItemDisplay

  /**
   * Getter for whether the step is in a valid state (completed).
   */
  isCompleted(): boolean

  /**
   * Whether this step caused a short-circuit.
   * Used by breakers.
   */
  isShortCircuited?(): boolean

  /**
   * Validates the step to determine whether continuing is possible.
   */
  validate?(): boolean
}

/**
 * Step position info.
 */
export interface StepPosition {
  /**
   * The step's index.
   */
  index: number

  /**
   * Whether the step is the first step.
   */
  first: boolean

  /**
   * Whether the step is the last step.
   */
  last: boolean
}

/**
 * Ports and state reader.
 */
export interface StepViewModelPorts extends StepViewModelStateReader {
  /**
   * Gets the index for the specified step.
   *
   * @param that
   */
  positionOf(that: StepViewModel): StepPosition

  /**
   * Getter for the currently active step (highlighted in the outline).
   */
  activeStep(): StepViewModel | null

  /**
   * Sets the specified step as the active one.
   *
   * @param that
   * @param direction If specified, overrides the navigation direction.
   */
  setActiveStep(that: StepViewModel, direction?: StepNavigationDirection): void
}

/**
 * A step in the outline.
 */
export class StepViewModel {
  /**
   * ID of the step.
   */
  readonly stepId: string

  constructor(private readonly _adapter: StepViewModelPorts) {
    this.stepId = this._adapter.stepId
  }

  /**
   * Whether the currently active step is this one.
   */
  @computed
  get isActive() {
    return this._adapter.activeStep() === this
  }

  /**
   * Whether the step causes a short-circuit.
   */
  @computed
  get isShortCircuited() {
    return Boolean(this._adapter.isShortCircuited?.())
  }

  /**
   * The index for this step.
   */
  @computed.struct
  get position() {
    return this._adapter.positionOf(this)
  }

  /**
   * Whether the step is completed.
   */
  @computed
  get isCompleted() {
    return this._adapter.isCompleted()
  }

  /**
   * Getter for the outline item display.
   *
   * @private
   */
  @computed
  get outlineDisplay() {
    return this._adapter.outlineItemDisplay()
  }

  /**
   * Activates this step, making it the active one.
   */
  @action.bound
  becomeActive(direction?: StepNavigationDirection) {
    this._adapter.setActiveStep(this, direction)
  }

  /**
   * Validates the step to determine whether continuing is possible.
   */
  @action.bound
  validate() {
    return this._adapter.validate?.() ?? true
  }
}

/**
 * Job drafting progress.
 */
export interface Progress {
  /**
   * Current step number (1-indexed).
   */
  currentStep: number
  /**
   * Total number of steps.
   */
  total: number
  /**
   * Number of completed steps.
   */
  completed: number
  /**
   * The percentage of completed steps, in whole numbers.
   */
  percent: number
}
